import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material";
import {
	RemoveRedEyeOutlined,
	ReceiptLongOutlined,
	HandshakeOutlined,
	AssignmentTurnedInOutlined,
	SwapHorizOutlined,
	PlaylistAddOutlined,
	TimelineOutlined,
	FolderOpenOutlined,
	DescriptionOutlined,
	PersonOutlined,
	PendingActionsOutlined,
} from "@mui/icons-material";
import React from "react";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { ModalSinisterValues } from "./Modals/ModalSinisterValues";
import { ModalSinisterDetails } from "./Modals/ModalSinisterDetails";
import { ModalNegotiateDeal } from "./Modals/ModalNegotiateDeal";
import { ModalLaunchFutureValue } from "./Modals/ModalLaunchFutureValue";
import { ModalUpdateCaseStatus } from "./Modals/ModalUpdateCaseStatus";
import { ModalChangeSector } from "./Modals/ModalChangeSector";
import { ModalChangeStage } from "./Modals/ModalChangeStage";
import { ModalChangeResponsible } from "./Modals/ModalChangeResponsible";
import { ModalRegisterInteraction } from "./Modals/ModalRegisterInteraction";
import { ModalCaseDocumentList } from "./Modals/ModalCaseDocumentList";
import { ModalDocumentsSinister } from "../../sinister/components/Modals/ModalDocumentsSinister";
import { SinisterSector } from "../constants/sectorOptions";
import { SinisterStage } from "../constants/stageOptions";
import { SinisterCaseStatus } from "../../../shared/services/api/sinister/SinisterNegotiationService";

interface Props {
	sinisterId: string;
	sector: SinisterSector;
	stage?: SinisterStage;
	caseStatus?: SinisterCaseStatus;
	busyProperty?: string;
	deadlineDate?: string | null;
	collectionResponsibleId?: string | null;
	collectionResponsibleName?: string | null;
	onChangeSector: (sector: SinisterSector) => void;
	onChangeStage: (stage: SinisterStage) => void;
	onInteractionRegistered: () => void;
	onStatusUpdated: () => void;
	onResponsibleUpdated: () => void;
}

export function CreditRecoveryRowMenu({
	sinisterId,
	sector,
	stage,
	caseStatus,
	busyProperty,
	deadlineDate,
	collectionResponsibleId,
	collectionResponsibleName,
	onChangeSector,
	onChangeStage,
	onInteractionRegistered,
	onStatusUpdated,
	onResponsibleUpdated,
}: Props) {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openDetailsModal, setOpenDetailsModal] = React.useState(false);
	const [openValuesModal, setOpenValuesModal] = React.useState(false);
	const [openDealModal, setOpenDealModal] = React.useState(false);
	const [openStatusModal, setOpenStatusModal] = React.useState(false);
	const [openSectorModal, setOpenSectorModal] = React.useState(false);
	const [openStageModal, setOpenStageModal] = React.useState(false);
	const [openResponsibleModal, setOpenResponsibleModal] = React.useState(false);
	const [openRegisterActionModal, setOpenRegisterActionModal] = React.useState(false);
	const [openDocumentsModal, setOpenDocumentsModal] = React.useState(false);
	const [openSinisterDocumentsModal, setOpenSinisterDocumentsModal] = React.useState(false);
	const [openLaunchFutureValueModal, setOpenLaunchFutureValueModal] = React.useState(false);
	const open = Boolean(anchorEl);
	const { isSuperAdmin } = useProfile();
	const isLegal = sector === "juridico";
	const canNegotiate =
		isSuperAdmin ||
		checkByProfileGuard(PermissionRolesEnum.CreditRecoveryOptions, isLegal ? PermissionActionsEnum.NegotiateLegal : PermissionActionsEnum.NegotiateCollection);
	const canUpdateStatus =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.CreditRecoveryOptions,
			isLegal ? PermissionActionsEnum.UpdateCaseStatusLegal : PermissionActionsEnum.UpdateCaseStatusCollection
		);
	const canChangeSector =
		isSuperAdmin ||
		checkByProfileGuard(PermissionRolesEnum.CreditRecoveryOptions, isLegal ? PermissionActionsEnum.ChangeSectorLegal : PermissionActionsEnum.ChangeSectorCollection);
	const canChangeStage =
		isSuperAdmin ||
		checkByProfileGuard(PermissionRolesEnum.CreditRecoveryOptions, isLegal ? PermissionActionsEnum.ChangeStageLegal : PermissionActionsEnum.ChangeStageCollection);
	const canRegisterInteraction =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.CreditRecoveryOptions,
			isLegal ? PermissionActionsEnum.RegisterInteractionLegal : PermissionActionsEnum.RegisterInteractionCollection
		);
	const canChangeResponsible =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.CreditRecoveryOptions,
			isLegal ? PermissionActionsEnum.ChangeResponsibleLegal : PermissionActionsEnum.ChangeResponsibleCollection
		);
	const canLaunchFutureValue =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.CreditRecoveryOptions,
			isLegal ? PermissionActionsEnum.LaunchFutureValueLegal : PermissionActionsEnum.LaunchFutureValueCollection
		);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	return (
		<>
			<IconButton size="small" onClick={handleClick} aria-label="Ações">
				<MoreVertIcon fontSize="small" />
			</IconButton>

			<Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ dense: true, sx: { py: 0 } }}>
				<ListSubheader sx={{ lineHeight: "32px" }}>Visualização</ListSubheader>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenDetailsModal(true);
					}}
				>
					<ListItemIcon>
						<RemoveRedEyeOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Detalhar sinistro</ListItemText>
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenValuesModal(true);
					}}
				>
					<ListItemIcon>
						<ReceiptLongOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Detalhar valores</ListItemText>
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenDocumentsModal(true);
					}}
				>
					<ListItemIcon>
						<FolderOpenOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Ver documentos</ListItemText>
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenSinisterDocumentsModal(true);
					}}
				>
					<ListItemIcon>
						<DescriptionOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Documentos do sinistro</ListItemText>
				</MenuItem>

				<Divider />
				<ListSubheader sx={{ lineHeight: "32px" }}>Ações</ListSubheader>

				<Tooltip title={canNegotiate ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canNegotiate}
							onClick={() => {
								handleClose();
								setOpenDealModal(true);
							}}
						>
							<ListItemIcon>
								<HandshakeOutlined fontSize="small" color={canNegotiate ? "primary" : "inherit"} />
							</ListItemIcon>
							<ListItemText primaryTypographyProps={{ fontWeight: 700, color: canNegotiate ? "primary" : "inherit" }}>Negociar</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canChangeStage ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canChangeStage}
							onClick={() => {
								handleClose();
								setOpenStageModal(true);
							}}
						>
							<ListItemIcon>
								<TimelineOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Alterar etapa</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canUpdateStatus ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canUpdateStatus}
							onClick={() => {
								handleClose();
								setOpenStatusModal(true);
							}}
						>
							<ListItemIcon>
								<AssignmentTurnedInOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Atualizar status</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canChangeSector ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canChangeSector}
							onClick={() => {
								handleClose();
								setOpenSectorModal(true);
							}}
						>
							<ListItemIcon>
								<SwapHorizOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Mudar de setor</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canRegisterInteraction ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canRegisterInteraction}
							onClick={() => {
								handleClose();
								setOpenRegisterActionModal(true);
							}}
						>
							<ListItemIcon>
								<PlaylistAddOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Registrar ação</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canChangeResponsible ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canChangeResponsible}
							onClick={() => {
								handleClose();
								setOpenResponsibleModal(true);
							}}
						>
							<ListItemIcon>
								<PersonOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Alterar responsável</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canLaunchFutureValue ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canLaunchFutureValue}
							onClick={() => {
								handleClose();
								setOpenLaunchFutureValueModal(true);
							}}
						>
							<ListItemIcon>
								<PendingActionsOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Lançar valor futuro</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>

			<ModalSinisterDetails open={openDetailsModal} sinisterId={sinisterId} onClose={() => setOpenDetailsModal(false)} />
			<ModalSinisterValues open={openValuesModal} sinisterId={sinisterId} onClose={() => setOpenValuesModal(false)} />
			<ModalCaseDocumentList open={openDocumentsModal} sinisterId={sinisterId} onClose={() => setOpenDocumentsModal(false)} />
			<ModalDocumentsSinister
				isOpen={openSinisterDocumentsModal}
				onClose={() => setOpenSinisterDocumentsModal(false)}
				id={sinisterId}
				busyProperty={busyProperty ?? ""}
				showButtonAttachment={false}
			/>
			<ModalNegotiateDeal open={openDealModal} sinisterId={sinisterId} onClose={() => setOpenDealModal(false)} />
			<ModalLaunchFutureValue open={openLaunchFutureValueModal} sinisterId={sinisterId} onClose={() => setOpenLaunchFutureValueModal(false)} />
			<ModalUpdateCaseStatus
				open={openStatusModal}
				sinisterId={sinisterId}
				currentStatus={caseStatus}
				deadlineDate={deadlineDate}
				onClose={() => setOpenStatusModal(false)}
				onUpdated={onStatusUpdated}
			/>
			<ModalChangeSector
				open={openSectorModal}
				sinisterId={sinisterId}
				currentSector={sector}
				onClose={() => setOpenSectorModal(false)}
				onConfirm={(newSector) => {
					onChangeSector(newSector);
					onResponsibleUpdated();
				}}
			/>
			<ModalChangeResponsible
				open={openResponsibleModal}
				sinisterId={sinisterId}
				sector={sector}
				currentResponsibleId={collectionResponsibleId}
				currentResponsibleName={collectionResponsibleName}
				onClose={() => setOpenResponsibleModal(false)}
				onConfirm={onResponsibleUpdated}
			/>
			<ModalChangeStage
				open={openStageModal}
				sinisterId={sinisterId}
				sector={sector}
				currentStage={stage}
				onClose={() => setOpenStageModal(false)}
				onConfirm={onChangeStage}
			/>
			<ModalRegisterInteraction
				open={openRegisterActionModal}
				sinisterId={sinisterId}
				onClose={() => setOpenRegisterActionModal(false)}
				onRegistered={() => {
					setOpenRegisterActionModal(false);
					onInteractionRegistered();
				}}
			/>
		</>
	);
}
